import { FC, useCallback, useMemo } from 'react';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { AIRCreditUsageTrackingIds } from '../tracking-ids';
import { LocationCreditData } from '../types';
import { getAICallCreditAlertContent } from '../utils';
import { useAICallCreditData } from './use-ai-call-credit-data';
import { useAIRCreditCriticalBannerDismiss, useAIRCreditWarningBannerDismiss } from './use-air-credit-banner-dismiss';

/**
 * App-wide global banners for AI call credit alerts.
 *
 * Renders two independent banners:
 * 1. Warning banner (80–94% usage) — dismissed until next billing cycle (persisted in localStorage).
 * 2. Critical/Exhausted banner (95%+) — dismissed until re-login (store resets on logout).
 *
 * Both banners are gated behind the `air-credit-enforcement` wetag per location.
 */
export const AICallCreditGlobalBanner: FC = () => {
  const { selectedLocationIds } = useAppScopeStore();

  const [isLimitEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  const { exhaustedLocations, criticalLocations, warningLocations, isLoading } = useAICallCreditData({
    locationIds: isLimitEnforcementEnabled ? selectedLocationIds : [],
  });

  const isMultiLocation = selectedLocationIds.length > 1;

  if (!isLimitEnforcementEnabled || isLoading) {
    return null;
  }

  return (
    <>
      <WarningBanner warningLocations={warningLocations} isMultiLocation={isMultiLocation} />
      <CriticalExhaustedBanner
        exhaustedLocations={exhaustedLocations}
        criticalLocations={criticalLocations}
        isMultiLocation={isMultiLocation}
      />
    </>
  );
};

// ─── Warning Banner ──────────────────────────────────────────────────────────

interface WarningBannerProps {
  warningLocations: LocationCreditData[];
  isMultiLocation: boolean;
}

const WarningBanner: FC<WarningBannerProps> = ({ warningLocations, isMultiLocation }) => {
  const { navigate } = useSettingsNavigate();
  const { undismissedLocations, dismissForLocations } = useAIRCreditWarningBannerDismiss(warningLocations);

  const content = useMemo(
    () =>
      getAICallCreditAlertContent({
        exhaustedLocations: [],
        criticalLocations: [],
        warningLocations: undismissedLocations,
        isMultiLocation,
      }),
    [undismissedLocations, isMultiLocation]
  );

  const handleDismiss = useCallback(() => {
    dismissForLocations(undismissedLocations);
  }, [undismissedLocations, dismissForLocations]);

  if (!content || undismissedLocations.length === 0) {
    return null;
  }

  return (
    <GlobalBannerDynamic
      id='ai-call-credit-warning-banner'
      data-testid='ai-call-credit-warning-banner'
      type={content.bannerType}
      isDismissible
      onDismiss={handleDismiss}
      title={content.title}
      message={content.description}
      action={{
        label: content.ctaLabel,
        onClick: () => navigate({ to: '/subscriptions/feature/:id', params: { id: 'ai-receptionist' } }),
      }}
      trackingId={AIRCreditUsageTrackingIds.globalWarningBannerCTA}
      dismissBtnTrackingId={AIRCreditUsageTrackingIds.globalWarningBannerDismiss}
    />
  );
};

// ─── Critical / Exhausted Banner ─────────────────────────────────────────────

interface CriticalExhaustedBannerProps {
  exhaustedLocations: LocationCreditData[];
  criticalLocations: LocationCreditData[];
  isMultiLocation: boolean;
}

const CriticalExhaustedBanner: FC<CriticalExhaustedBannerProps> = ({
  exhaustedLocations,
  criticalLocations,
  isMultiLocation,
}) => {
  const { navigate } = useSettingsNavigate();
  const { undismissedLocations, dismissForLocations } = useAIRCreditCriticalBannerDismiss([
    ...exhaustedLocations,
    ...criticalLocations,
  ]);

  const undismissedExhausted = useMemo(
    () => exhaustedLocations.filter((loc) => undismissedLocations.some((u) => u.locationId === loc.locationId)),
    [exhaustedLocations, undismissedLocations]
  );

  const undismissedCritical = useMemo(
    () => criticalLocations.filter((loc) => undismissedLocations.some((u) => u.locationId === loc.locationId)),
    [criticalLocations, undismissedLocations]
  );

  const content = useMemo(
    () =>
      getAICallCreditAlertContent({
        exhaustedLocations: undismissedExhausted,
        criticalLocations: undismissedCritical,
        warningLocations: [],
        isMultiLocation,
      }),
    [undismissedExhausted, undismissedCritical, isMultiLocation]
  );

  const handleDismiss = useCallback(() => {
    dismissForLocations([...undismissedExhausted, ...undismissedCritical]);
  }, [undismissedExhausted, undismissedCritical, dismissForLocations]);

  if (!content || (undismissedExhausted.length === 0 && undismissedCritical.length === 0)) {
    return null;
  }

  return (
    <GlobalBannerDynamic
      id='ai-call-credit-critical-banner'
      data-testid='ai-call-credit-critical-banner'
      type={content.bannerType}
      isDismissible
      onDismiss={handleDismiss}
      title={content.title}
      message={content.description}
      action={{
        label: content.ctaLabel,
        onClick: () =>
          navigate({
            to: '/subscriptions/feature/:id',
            params: { id: 'ai-receptionist' },
            // If the banner represents a critical status, we want to trigger the upgrade modal automatically.
            search: content.severity === 'critical' ? { openSubscriptionModal: true } : undefined,
          }),
      }}
      trackingId={AIRCreditUsageTrackingIds.globalCriticalBannerCTA}
      dismissBtnTrackingId={AIRCreditUsageTrackingIds.globalCriticalBannerDismiss}
    />
  );
};
